import { useEffect, useState, ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaBell, FaCheck } from 'react-icons/fa';
import api from '@/services/api';
import { useToast } from '@/components/common/ToastProvider';
import { User } from '@/types';
import { useClickOutside } from '@/hooks/useClickOutside';

interface ChannelNotificationSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  channelName: string;
  isVoice: boolean;
}

interface NotificationConfig {
  id: string;
  notifyOnMessage: boolean;
  notifyOnMention: boolean;
  notifyOnFriendJoin: boolean; // This will be ignored but kept for type safety until backend is updated
  notifyOnVoiceJoin: boolean;
  notifyOnSpecificUsers: string | null;
}

interface InternalConfig extends Omit<NotificationConfig, 'notifyOnFriendJoin'> {
  specificUsersArray: string[]; // For internal use in the component
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <div className="bg-brand-surface/80 dark:bg-[#252350]/80 border border-brand-outline-variant/60 dark:border-[#3730a3] rounded-2xl p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-semibold text-brand-on-background dark:text-[#e0e7ff]">
            {title}
          </div>
          {description && (
            <div className="text-xs text-brand-outline dark:text-[#c4b5fd] mt-1">
              {description}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function ToggleSwitch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-3 cursor-pointer group select-none">
      <div className="relative w-12 h-7">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div
          className={`w-12 h-7 rounded-full transition-colors ${
            checked ? 'bg-brand-primary' : 'bg-brand-surface-variant dark:bg-[#312e81]'
          }`}
        />
        <div
          className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
            checked ? 'translate-x-5' : ''
          }`}
        />
      </div>
      <span className="text-sm font-medium text-brand-on-surface dark:text-[#e0e7ff] group-hover:text-white transition-colors">
        {label}
      </span>
    </label>
  );
}

export default function ChannelNotificationSettings({
  isOpen,
  onClose,
  channelId,
  channelName,
  isVoice,
}: ChannelNotificationSettingsProps) {
  const [config, setConfig] = useState<InternalConfig | null>(null);
  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const modalRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalRef, onClose);

  useEffect(() => {
    if (isOpen && channelId) {
      loadSettingsAndMembers();
    }
  }, [isOpen, channelId]);

  const loadSettingsAndMembers = async () => {
    try {
      setLoading(true);

      const settingsResponse = await api.get(`/notifications/channels/${channelId}`);
      const membersResponse = await api.get(`/channels/${channelId}/members`);

      const fetchedConfig = settingsResponse.data.config;
      const specificUsersArray = fetchedConfig.notifyOnSpecificUsers
        ? JSON.parse(fetchedConfig.notifyOnSpecificUsers)
        : [];

      setConfig({
        ...fetchedConfig,
        specificUsersArray,
      });

      setMembers(membersResponse.data.users || []);
    } catch (error) {
      console.error('Error loading notification settings or members:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      setSaving(true);

      const notifyOnSpecificUsers =
        config.specificUsersArray && config.specificUsersArray.length > 0
          ? JSON.stringify(config.specificUsersArray)
          : null;

      await api.put(`/notifications/channels/${channelId}`, {
        notifyOnMessage: config.notifyOnMessage,
        notifyOnMention: config.notifyOnMention,
        notifyOnVoiceJoin: config.notifyOnVoiceJoin,
        notifyOnSpecificUsers,
      });
      toast.success('Notification settings updated');
      onClose();
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleUserSelectionChange = (userId: string) => {
    if (!config) return;
    const newSelection = [...config.specificUsersArray];
    const index = newSelection.indexOf(userId);

    if (index > -1) {
      newSelection.splice(index, 1);
    } else {
      newSelection.push(userId);
    }
    setConfig({ ...config, specificUsersArray: newSelection });
  };

  if (!isOpen) return null;

  const isEveryone = config?.notifyOnVoiceJoin && config.specificUsersArray.length === 0;
  const isSpecific = config?.notifyOnVoiceJoin && config.specificUsersArray.length > 0;

  return createPortal(
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-brand-surface dark:bg-[#1a1740] border border-brand-outline-variant/60 dark:border-[#3730a3] rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-m3-dialog"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-brand-outline-variant/60 dark:border-[#3730a3] sticky top-0 bg-brand-surface dark:bg-[#1a1740]">
          <div className="flex items-center gap-2">
            <FaBell className="text-brand-primary dark:text-brand-primary" size={20} />
            <h2 className="text-xl font-bold text-brand-on-background dark:text-[#e0e7ff]">
              Notification Settings
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close settings"
            className="p-2 rounded-full text-brand-outline hover:text-white hover:bg-white/10 active:scale-95 transition-all"
          >
            <FaTimes
              className="text-brand-on-surface-variant dark:text-[#c4b5fd]"
              size={18}
            />
          </button>
        </div>

        {/* Channel name */}
        <div className="px-4 pt-4 pb-2">
          <div className="text-sm text-brand-on-surface-variant dark:text-[#c4b5fd]">
            Channel
          </div>
          <div className="text-lg font-semibold text-brand-on-background dark:text-[#e0e7ff]">
            #{channelName}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {loading ? (
            <div className="text-center py-8 text-brand-on-surface-variant dark:text-brand-on-surface-variant">
              Loading...
            </div>
          ) : config ? (
            <>
              {!isVoice && (
                <>
                  <SectionCard
                    title="Message Notifications"
                    description="Get notified when messages are posted in this channel"
                  >
                    <ToggleSwitch
                      checked={config.notifyOnMessage}
                      onChange={(v) => setConfig({ ...config, notifyOnMessage: v })}
                      label="Notify on all messages"
                    />
                  </SectionCard>

                  <SectionCard
                    title="Mention Notifications"
                    description="Get notified when you are mentioned in this channel"
                  >
                    <ToggleSwitch
                      checked={config.notifyOnMention}
                      onChange={(v) => setConfig({ ...config, notifyOnMention: v })}
                      label="Notify on mentions"
                    />
                  </SectionCard>
                </>
              )}

              {isVoice && (
                <SectionCard
                  title="User Activity Notifications"
                  description="Get notified when people join this channel"
                >
                  <ToggleSwitch
                    checked={config.notifyOnVoiceJoin}
                    onChange={(v) => {
                      const newConfig = { ...config, notifyOnVoiceJoin: v };
                      // Default to 'everyone' if turning on, clear selection if turning off
                      if (!v) {
                        newConfig.specificUsersArray = [];
                      }
                      setConfig(newConfig);
                    }}
                    label="Notify when someone joins voice"
                  />
                  {config.notifyOnVoiceJoin && (
                    <div className="ml-6 mt-4 space-y-3">
                      <label
                        htmlFor="notif-scope-everyone"
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <input
                          id="notif-scope-everyone"
                          type="radio"
                          name="user-notification-scope"
                          checked={isEveryone}
                          onChange={() => setConfig({ ...config, specificUsersArray: [] })}
                          className="form-radio h-4 w-4 text-brand-primary bg-brand-surface dark:bg-brand-surface-variant border-brand-outline-variant dark:border-gray-500 focus:ring-blue-500"
                        />
                        <span className="text-sm text-brand-on-surface dark:text-brand-on-surface-variant">
                          Everyone
                        </span>
                      </label>
                      <label
                        htmlFor="notif-scope-specific"
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <input
                          id="notif-scope-specific"
                          type="radio"
                          name="user-notification-scope"
                          checked={isSpecific}
                          onChange={() =>
                            setConfig({
                              ...config,
                              specificUsersArray:
                                config.specificUsersArray.length > 0
                                  ? config.specificUsersArray
                                  : [],
                            })
                          }
                          className="form-radio h-4 w-4 text-brand-primary bg-brand-surface dark:bg-brand-surface-variant border-brand-outline-variant dark:border-gray-500 focus:ring-blue-500"
                        />
                        <span className="text-sm text-brand-on-surface dark:text-brand-on-surface-variant">
                          Specific Users
                        </span>
                      </label>

                      {isSpecific && (
                        <div className="pl-6 pt-2 max-h-48 overflow-y-auto border-l border-brand-outline-variant dark:border-brand-outline-variant">
                          {members.length > 0 ? (
                            members.map((member) => (
                              <label
                                key={member.id}
                                htmlFor={`notif-user-${member.id}`}
                                className="flex items-center gap-3 py-1 cursor-pointer"
                              >
                                <input
                                  id={`notif-user-${member.id}`}
                                  name={`notif-user-${member.id}`}
                                  type="checkbox"
                                  checked={config.specificUsersArray.includes(member.id)}
                                  onChange={() => handleUserSelectionChange(member.id)}
                                  className="form-checkbox h-4 w-4 text-brand-primary bg-brand-surface dark:bg-brand-surface-variant border-brand-outline-variant dark:border-gray-500 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm text-brand-on-surface dark:text-brand-on-surface-variant">
                                  {member.username}
                                </span>
                              </label>
                            ))
                          ) : (
                            <div className="text-xs text-brand-outline dark:text-brand-on-surface-variant">
                              No members found in this channel.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </SectionCard>
              )}

              <div className="bg-brand-primary/10 dark:bg-brand-primary/20 border border-brand-primary/30 dark:border-brand-primary/30 rounded-lg p-3">
                <div className="text-xs text-brand-primary dark:text-blue-200">
                  ℹ️ Notification settings are per-channel. Global settings can be changed in your
                  user preferences.
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-brand-on-surface-variant dark:text-brand-on-surface-variant">
              Failed to load settings
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-brand-outline-variant/60 dark:border-brand-outline sticky bottom-0 bg-brand-surface dark:bg-[#1a1740]">
          <button
            onClick={onClose}
            className="btn btn-secondary flex-1 py-2.5 rounded-full font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary flex-1 py-2.5 rounded-full font-medium flex items-center justify-center gap-2"
          >
            <FaCheck size={16} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
